import moment from 'moment';

import { useLicenseInfo } from '@/react/portainer/licenses/use-license.service';
import { pluralize } from '@/portainer/helpers/strings';
import { LicenseType } from '@/react/portainer/licenses/types';
import {
  licenseRenewURL,
  contactPortainerURL,
} from '@/react/portainer/licenses/constants';

import { Alert } from '@@/Alert';
import { ExternalLink } from '@@/ExternalLink';

export function LicenseExpirationPanelContainer() {
  const licenseInfoQuery = useLicenseInfo();

  if (licenseInfoQuery.isLoading || !licenseInfoQuery.info) {
    return null;
  }

  const nextLicenseExpiryUnix = moment.unix(
    licenseInfoQuery.info?.expiresAt || 0
  );
  const remainingDays = nextLicenseExpiryUnix.diff(
    moment().startOf('day'),
    'days'
  );
  const noValidLicense = !licenseInfoQuery.info?.valid;

  return (
    <LicenseExpirationPanel
      remainingDays={remainingDays}
      noValidLicense={noValidLicense}
      licenseType={licenseInfoQuery.info.type}
    />
  );
}

interface Props {
  remainingDays: number;
  noValidLicense?: boolean;
  licenseType: LicenseType;
}

export function LicenseExpirationPanel({
  remainingDays,
  noValidLicense,
  licenseType,
}: Props) {
  if (remainingDays > 30) {
    return null;
  }

  const expirationMessage = buildMessage(
    remainingDays,
    noValidLicense,
    licenseType
  );

  return (
    <Alert color="warn" className="mt-3">
      <span>{expirationMessage}</span>
    </Alert>
  );
}

function buildMessage(
  days: number,
  noValidLicense?: boolean,
  licenseType?: LicenseType
) {
  let contactMessage = (
    <>
      <ExternalLink
        to={contactPortainerURL}
        data-cy="contact-portainer-link"
        className="!text-inherit underline hover:!text-inherit"
      >
        Contact Portainer
      </ExternalLink>{' '}
      to purchase a license.
    </>
  );
  if (licenseType === LicenseType.Free) {
    contactMessage = (
      <>
        Please fill out <a href={licenseRenewURL}>the form</a> to upgrade your
        license.
      </>
    );
  }

  if (noValidLicense) {
    return (
      <>
        You have no valid licenses and will need to supply one on next login.{' '}
        {contactMessage}
      </>
    );
  }

  const expiringText = buildExpiringText(days);

  return (
    <>
      One or more of your licenses {expiringText}. {contactMessage}
    </>
  );

  function buildExpiringText(days: number) {
    if (days < 0) {
      return 'has expired';
    }

    if (days === 0) {
      return 'expires TODAY';
    }

    return `will expire in ${days} ${pluralize(days, 'day')}`;
  }
}
